import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  TextField, 
  Card, 
  CardContent, 
  Grid,
  Button,
  Stack,
  CardHeader,
  Autocomplete,
  Typography,
  Alert,
  Snackbar,
  Checkbox,
  Switch,
  Box,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from 'react-i18next';
import { client, Market, Instance } from '../../app/services/api/orderManagementClient';
import { Tenant, SystemResponse } from '../../app/services/api/generated';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import useLogError from '../../hooks/useLogError';
import useNswagClient from '../../hooks/api/useNswagClient';
import { ContainerFillMethodEnum } from './enums/ContainerFillMethodEnum';

const MarketForm = () => {
  const { t } = useTranslation('markets');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo, selectedSystem } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const { getTenants, getSystems, getFranchiseByUuid } = useNswagClient();
  const isEdit = Boolean(id);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [systems, setSystems] = useState<SystemResponse[]>([]);
  const [selectedSystems, setSelectedSystems] = useState<SystemResponse[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const [formData, setFormData] = useState<Market>({
    customerTypeId: 0,
    instanceName: '',
    companyId: 0,
    name: '',
    timezone: '',
    timezoneOffset: 0,
    currencyCode: '',
    containerFillMethod: 0,
    tenantId: undefined,
    systemIds: [],
    isParEnabled: false,
    uomGroup: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const containerFillMethodOptions = [
    { value: ContainerFillMethodEnum.Volume, label: 'Volume' },
    { value: ContainerFillMethodEnum.Unit, label: 'Unit' },
    { value: ContainerFillMethodEnum.ProductPalletQuantity, label: 'Product Pallet Quantity' },
  ];

  const isOfd = selectedSystem?.name === 'Orderly for Distributors';
  const isOfs = selectedSystem?.name === 'Orderly for Stores';

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchTenants = async () => {
    try {
      const result = await getTenants();
      if (result?.data?.tenants) {
        setTenants(result.data.tenants);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchSystems = async () => {
    try {
      const result = await getSystems();
      if (result?.data?.systems) {
        setSystems(result.data.systems);
      }
    } catch (error) {
      console.error('Error fetching systems:', error);
    }
  };

  const fetchMarket = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const result = await client.getMarket(id);
      if (result.success && result.data) {
        const market = result.data;
        if (market.uuid) {
          try {
            const franchiseResult = await getFranchiseByUuid(market.uuid);
            if (franchiseResult?.data) {
              market.tenantId = franchiseResult.data.tenantId ?? undefined;
              market.systemIds = franchiseResult.data.systemIds ?? [];
            }
          } catch (franchiseError) {
            console.error('Error fetching franchise data from IM:', franchiseError);
          }
        }
        setFormData(market);
      }
    } catch (error) {
      console.error('Error fetching market:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
    fetchTenants();
    fetchSystems();
    if (isEdit) {
      fetchMarket();
    }
  }, [id, isEdit]);

  // Sync selectedSystems state when systems list and formData are both loaded
  useEffect(() => {
    if (systems.length > 0 && formData.systemIds && formData.systemIds.length > 0) {
      const matched = systems.filter(s => formData.systemIds!.includes(s.id!));
      setSelectedSystems(matched);
    }
  }, [systems, formData.systemIds]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData?.name?.trim()) {
      newErrors.name = t('nameRequired');
    }

    if (!formData?.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    if (!formData?.timezone?.trim()) {
      newErrors.timezone = t('timezoneRequired');
    } else {
      const timezone = formData?.timezone?.trim()?.toUpperCase();
      const validTimezones = ['UTC', 'CET', 'EET', 'BST', 'PST', 'EST', 'MST', 'CST', 'JST', 'AEST'];
      if (timezone.length !== 3 || !validTimezones.includes(timezone)) {
        newErrors.timezone = t('timezoneInvalid');
      }
    }

    if (!formData?.currencyCode?.trim()) {
      newErrors.currencyCode = t('currencyCodeRequired');
    } else {
      const currency = formData?.currencyCode?.trim()?.toUpperCase();
      if (currency.length !== 3) {
        newErrors.currencyCode = t('currencyCodeInvalid');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof Market) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value = event.target.value;
    
    if (field === 'timezone' || field === 'currencyCode') {
      value = value?.toUpperCase();
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: field === 'timezoneOffset' ? (value ? parseFloat(value) : undefined) : value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
    }));
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleTenantChange = (_event: React.SyntheticEvent, value: Tenant | null) => {
    setFormData(prev => ({
      ...prev,
      tenantId: value?.id || undefined,
    }));
  };

  const handleSystemsChange = (_event: React.SyntheticEvent, value: SystemResponse[]) => {
    setSelectedSystems(value);
    setFormData(prev => ({
      ...prev,
      systemIds: value.map(s => s.id!).filter((id): id is number => id !== undefined && id !== null),
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarket(id!, formData);
        if (result.success) {
          setMessage(t('updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            navigate('/markets');
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        const result = await client.createMarket(formData);
        if (result.success) {
          setMessage(t('createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            navigate('/markets');
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('updateErrorMessage') : t('createErrorMessage')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/markets');
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'MarketUpdate' : 'MarketCreate', 'ConfigurationAdmin']);
  const canViewMarket = hasPermissionTo(['MarketView', 'ConfigurationAdmin']);

  if (!canViewMarket) {
    return (
      <PageArea>
        <PageTitle>{t('accessDenied')}</PageTitle>
        <Typography>{t('insufficientPermissions')}</Typography>
      </PageArea>
    );
  }

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{isEdit ? t('editMarket') : t('createMarket')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={8}>
        {/* Basic Market Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('basicInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid
              container
              spacing={8}>
              <Grid
                item
                xs={12}>
                <TextField
                  fullWidth
                  label={t('name')}
                  placeholder="Enter market name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find(inst => inst.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      placeholder="Search and select an instance..."
                      error={!!errors.companyId}
                      helperText={errors.companyId}
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  noOptionsText={t('noInstancesFound')}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Location & Currency Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('locationAndCurrency')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid
              container
              spacing={8}>
              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('timezone')}
                  placeholder="UTC, CET, EET, BST"
                  value={formData.timezone}
                  onChange={handleInputChange('timezone')}
                  error={!!errors.timezone}
                  helperText={errors.timezone || t('timezoneHelp')}
                  inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('currencyCode')}
                  placeholder="USD, EUR, GBP"
                  value={formData.currencyCode}
                  onChange={handleInputChange('currencyCode')}
                  error={!!errors.currencyCode}
                  helperText={errors.currencyCode || t('currencyHelp')}
                  inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <TextField
                  fullWidth
                  label={t('timezoneOffset')}
                  type="number"
                  placeholder="0"
                  value={formData.timezoneOffset ?? ''}
                  onChange={handleInputChange('timezoneOffset')}
                  helperText={t('timezoneOffsetHelp')}
                  inputProps={{ step: 0.5, min: -12, max: 14 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Ordering Configuration Card */}
        {isOfd && (
          <Card elevation={1}>
            <CardHeader
              title={t('ordering')}
              sx={{ color: 'text.primary' }}
            />
            <CardContent sx={{ p: 8 }}>
              <Grid
                container
                spacing={8}>
                <Grid
                  item
                  xs={12}
                  md={6}>
                  <Autocomplete
                    options={containerFillMethodOptions}
                    getOptionLabel={option => option.label}
                    value={containerFillMethodOptions.find(opt => opt.value === formData.containerFillMethod) || null}
                    onChange={(_, value) => setFormData(prev => ({ ...prev, containerFillMethod: value?.value }))}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={t('containerFillMethod')}
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    clearOnBlur
                    selectOnFocus
                    handleHomeEndKeys
                    sx={{ '& .MuiAutocomplete-inputRoot': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* PAR Configuration Card */}
        {isOfs && (
          <Card elevation={1}>
            <CardHeader
              title={t('parOrdering')}
              sx={{ color: 'text.primary' }}
            />
            <CardContent sx={{ p: 8 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: 1,
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: 2,
                  }}>
                  <Switch
                    checked={formData.isParEnabled ?? false}
                    onChange={() => setFormData(prev => ({ ...prev, isParEnabled: !prev.isParEnabled }))}
                  />
                  <Typography variant="textSM">{t('isParEnabled')}</Typography>
                </Box>
                <Typography
                  variant="textXS"
                  color="text.secondary">
                  {t('isParEnabledHelp')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Tenant & Systems Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('tenantAndSystems')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid
              container
              spacing={8}>
              <Grid
                item
                xs={12}>
                <Autocomplete
                  options={tenants}
                  getOptionLabel={(option) => option.name || ''}
                  value={tenants.find(ten => ten.id === formData.tenantId) || null}
                  onChange={handleTenantChange}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('tenant')}
                      placeholder={t('tenantPlaceholder')}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  noOptionsText={t('noTenantsFound')}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Autocomplete
                  multiple
                  options={systems}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedSystems}
                  onChange={handleSystemsChange}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('systems')}
                      placeholder={t('systemsPlaceholder')}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  noOptionsText={t('noSystemsFound')}
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons Card */}
        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                {t('cancel')}
              </Button>
              {userCanSave && (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  sx={{ minWidth: 120, borderRadius: 2 }}
                >
                  {isSaving ? t('saving') : isEdit ? t('update') : t('create')}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default MarketForm;
